import { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { formatDecimal, formatDollars } from "@aclymatepackages/formatters";
import {
  Checkbox,
  DefaultPaper,
  LoadingButton
} from "@aclymatepackages/atoms";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArchiveIcon from "@mui/icons-material/Archive";
import CancelIcon from "@mui/icons-material/Cancel";

import { hexToRgba } from "@aclymatepackages/converters";

import FeatureErrorBoundary from "../../../../../atoms/FeatureErrorBoundary";
import usePlatformLayoutContext from "../../../../../../helpers/contexts/platformLayout";

import DuplicateClusterSlider from "./DuplicateClusterSlider";
import ZeroTxnClusterSlider from "./ZeroTxnClusterSlider";
import NaicsReviewSlider from "./NaicsReviewSlider";

import { useCleanupCandidates } from "./useCleanupCandidates";
import {
  confidenceTier,
  confidenceChipColor,
  matchBasisLabel,
  MATCH_LABEL_MAP,
  IMPACT_LABEL_MAP,
  reasonLabel,
  reasonChipColor,
  formatCategoryLabel,
  clusterImpactTier,
  clusterTotalEmissions,
  impactChipColor,
  buildImpactTooltip
} from "./helpers";

const CONFIDENCE_TOOLTIP =
  "How likely these are actually the same vendor, based on name similarity and AI matching. Higher means more likely to be a real duplicate.";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const CLEANUP_TABS = [
  { value: "duplicates", label: "Possible duplicates" },
  { value: "industries", label: "Industry suggestions" },
  { value: "empty", label: "No transactions" },
  { value: "archived", label: "Archived" }
];

const VALID_TAB_VALUES = CLEANUP_TABS.map(({ value }) => value);

const readTabFromSearch = (search) => {
  const fromUrl = new URLSearchParams(search).get("tab");
  return VALID_TAB_VALUES.includes(fromUrl) ? fromUrl : "duplicates";
};

const usePagination = (totalCount, defaultPerPage = 25) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPerPage);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= totalCount) {
      setPage(0);
    }
  }, [totalCount, page, rowsPerPage]);

  const onChangePage = (_, nextPage) => setPage(nextPage);
  const onChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const start = page * rowsPerPage;
  const end = start + rowsPerPage;

  return { page, rowsPerPage, start, end, onChangePage, onChangeRowsPerPage };
};

const PaginationControls = ({
  totalCount,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage
}) => (
  <TablePagination
    component="div"
    count={totalCount}
    page={page}
    onPageChange={onChangePage}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={onChangeRowsPerPage}
    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
  />
);

const ConfidenceChip = ({ confidence, matchBasis }) => {
  const tier = confidenceTier(confidence);
  const label = matchBasisLabel(matchBasis) || MATCH_LABEL_MAP[tier];
  return (
    <Tooltip title={CONFIDENCE_TOOLTIP} arrow>
      <Chip
        size="small"
        label={`${label} · ${Math.round(confidence * 100)}%`}
        color={confidenceChipColor(confidence)}
        variant={tier === "low" ? "outlined" : "filled"}
      />
    </Tooltip>
  );
};

const ImpactChip = ({ cluster }) => {
  const tier = clusterImpactTier(cluster);
  const totalEmissions = clusterTotalEmissions(cluster);
  return (
    <Tooltip title={buildImpactTooltip(cluster)} arrow>
      <Chip
        size="small"
        label={`${IMPACT_LABEL_MAP[tier]} · ${formatDecimal(totalEmissions)} tCO2e`}
        color={impactChipColor(cluster)}
        variant={tier === "low" ? "outlined" : "filled"}
      />
    </Tooltip>
  );
};

const NaicsReasonFilterChips = ({ selectedReason, setSelectedReason, counts }) => {
  const filters = [
    { label: "All", value: "all", count: counts.all },
    { label: "Low confidence", value: "low-confidence", count: counts["low-confidence"] },
    { label: "Merge conflicts", value: "merge-conflict", count: counts["merge-conflict"] },
    { label: "Tax/regulatory", value: "government-entity", count: counts["government-entity"] }
  ];

  return (
    <Grid container alignItems="center" spacing={1} wrap="wrap">
      {filters.map(({ label, value, count }) => (
        <Grid item key={`naics-reason-filter-${value}`}>
          <Chip
            label={`${label} (${count})`}
            color={selectedReason === value ? "primary" : "default"}
            variant={selectedReason === value ? "filled" : "outlined"}
            onClick={() => setSelectedReason(value)}
            size="small"
          />
        </Grid>
      ))}
    </Grid>
  );
};

const SearchInput = ({ value, onChange, placeholder }) => (
  <TextField
    size="small"
    fullWidth
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon fontSize="small" color="action" />
        </InputAdornment>
      )
    }}
  />
);

const SectionPaper = ({ title, subtitle, children, action }) => (
  <Paper>
    <Box p={3}>
      <Grid
        container
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        wrap="nowrap"
      >
        <Grid item xs>
          <Typography variant="h5">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Grid>
        {action && <Grid item>{action}</Grid>}
      </Grid>
      <Box mt={2}>{children}</Box>
    </Box>
  </Paper>
);

const EmptyState = ({ icon: Icon, message }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={3}
      style={{
        textAlign: "center",
        color: palette.text.secondary,
        border: `1px dashed ${palette.backgroundGray.dark}`,
        borderRadius: 8
      }}
    >
      <Icon aria-hidden="true" style={{ fontSize: 32, marginBottom: 8 }} />
      <Typography variant="body2">{message}</Typography>
    </Box>
  );
};

const clusterReviewButtonLabel = ({ isZeroTxnPair, isMerging, isActioned }) => {
  if (isActioned) {
    return "Done";
  }
  if (isMerging) {
    return isZeroTxnPair ? "Archiving…" : "Merging…";
  }
  return isZeroTxnPair ? "Confirm & Archive" : "Review & Merge";
};

const ClusterRow = ({ cluster, onReview, isMerging }) => {
  const { palette } = useTheme();
  const {
    vendors,
    confidence,
    matchBasis,
    reason,
    recommendedPrimaryId,
    status,
    isZeroTxnPair
  } = cluster;
  const primary =
    vendors.find(({ id }) => id === recommendedPrimaryId) || vendors[0];
  const others = vendors.filter(({ id }) => id !== primary.id);

  // isMerging covers the window between a merge/archive request firing and
  // its status update landing -- cluster.status stays "pending" the whole
  // time, so without this the row stays reviewable/re-actionable while the
  // first request is still in flight.
  const isActioned = status !== "pending" || isMerging;
  const statusLabel = {
    merged: "Merged",
    archived: "Archived",
    dismissed: "Dismissed"
  }[status];

  return (
    <Paper
      variant="outlined"
      style={{
        borderColor: isActioned
          ? palette.secondary.main
          : palette.backgroundGray.dark,
        opacity: isActioned ? 0.7 : 1
      }}
    >
      <Box p={2}>
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item xs>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle1">
                  {`${vendors.length} vendors flagged`}
                </Typography>
              </Grid>
              <Grid item>
                <ConfidenceChip confidence={confidence} matchBasis={matchBasis} />
              </Grid>
              <Grid item>
                <ImpactChip cluster={cluster} />
              </Grid>
              {statusLabel && (
                <Grid item>
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon fontSize="small" />}
                    label={statusLabel}
                    color="secondary"
                  />
                </Grid>
              )}
            </Grid>
            <Box mt={1}>
              <Typography variant="body2">
                <strong>{primary.name}</strong>
                {others.length
                  ? `  ←  ${others.map(({ name }) => name).join(", ")}`
                  : ""}
              </Typography>
              {reason && (
                <Typography variant="caption" color="textSecondary">
                  {reason}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant={isActioned ? "outlined" : "contained"}
              color="primary"
              size="small"
              onClick={() => onReview(cluster)}
              disabled={isActioned}
            >
              {clusterReviewButtonLabel({ isZeroTxnPair, isMerging, isActioned })}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const NaicsRow = ({ vendor, onReview }) => {
  const { palette } = useTheme();
  const {
    name,
    naicsTitle,
    emissionCategory,
    confidenceScore,
    transactionCount,
    dollarAmount,
    status,
    reason
  } = vendor;
  const displayIndustry = naicsTitle || formatCategoryLabel(emissionCategory);
  const isActioned = status !== "pending";
  const showReasonChip = reason === "merge-conflict" || reason === "government-entity";
  const statusLabel = {
    updated: "Updated",
    dismissed: "Dismissed"
  }[status];

  return (
    <Paper
      variant="outlined"
      style={{
        borderColor: isActioned
          ? palette.secondary.main
          : palette.backgroundGray.dark,
        opacity: isActioned ? 0.7 : 1
      }}
    >
      <Box p={2}>
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item xs>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle1">{name}</Typography>
              </Grid>
              <Grid item>
                <Chip
                  size="small"
                  label={
                    showReasonChip
                      ? reasonLabel(reason)
                      : `Confidence ${Math.round(confidenceScore * 100)}%`
                  }
                  color={reasonChipColor(reason)}
                  variant="outlined"
                />
              </Grid>
              {dollarAmount > 0 && (
                <Grid item>
                  <Chip
                    size="small"
                    label={`${formatDollars(dollarAmount)} exposure`}
                    color="default"
                    variant="outlined"
                  />
                </Grid>
              )}
              {statusLabel && (
                <Grid item>
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon fontSize="small" />}
                    label={statusLabel}
                    color="secondary"
                  />
                </Grid>
              )}
            </Grid>
            <Box mt={1}>
              <Typography variant="body2">{`Industry: ${displayIndustry}`}</Typography>
              {typeof transactionCount === "number" && (
                <Typography variant="caption" color="textSecondary">
                  {`${transactionCount} transaction${
                    transactionCount === 1 ? "" : "s"
                  }`}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant={isActioned ? "outlined" : "contained"}
              color="primary"
              size="small"
              onClick={() => onReview(vendor)}
              disabled={isActioned}
            >
              {isActioned ? "Done" : "Review"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const EmptyVendorRow = ({ vendor, isChecked, onToggleChecked, onArchive }) => {
  const { palette } = useTheme();
  const { name, naicsTitle, lastSeen, status, relationshipId } = vendor;
  const isArchived = status === "archived";

  return (
    <Paper
      variant="outlined"
      style={{
        borderColor: isArchived
          ? palette.secondary.main
          : palette.backgroundGray.dark,
        opacity: isArchived ? 0.7 : 1
      }}
    >
      <Box p={2}>
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Checkbox
              value={isChecked}
              editValue={() => onToggleChecked(relationshipId)}
              disabled={isArchived}
            />
          </Grid>
          <Grid item xs>
            <Typography variant="subtitle1">{name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {`Industry: ${naicsTitle}`}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {lastSeen
                ? `No transactions · last seen ${lastSeen}`
                : "No transactions"}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant={isArchived ? "outlined" : "contained"}
              color="primary"
              size="small"
              startIcon={<ArchiveIcon fontSize="small" />}
              onClick={() => onArchive(relationshipId)}
              disabled={isArchived}
            >
              {isArchived ? "Archived" : "Archive"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const EmptyVendorsBulkActionBar = ({
  checkedCount,
  isBulkArchiving,
  onBulkArchive,
  onClearSelection
}) => (
  <DefaultPaper>
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
      wrap="nowrap"
    >
      <Grid item>
        <Typography variant="h6">
          {`${checkedCount} vendor${
            checkedCount === 1 ? "" : "s"
          } selected`}
        </Typography>
      </Grid>
      <Grid item>
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid item>
            <LoadingButton
              isLoading={isBulkArchiving}
              onClick={onBulkArchive}
              variant="contained"
              color="primary"
              label={`Archive ${checkedCount} vendor${
                checkedCount === 1 ? "" : "s"
              }`}
            />
          </Grid>
          <Grid item>
            <Tooltip title="Clear selection">
              <span>
                <IconButton
                  onClick={onClearSelection}
                  disabled={isBulkArchiving}
                >
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </DefaultPaper>
);

const EmptyVendorsSelectAllHeader = ({
  allPageChecked,
  somePageChecked,
  onTogglePageSelectAll,
  visibleOnPageCount
}) => (
  <Box pl={1} pb={0.5}>
    <FormControlLabel
      control={
        <MuiCheckbox
          checked={allPageChecked}
          indeterminate={somePageChecked}
          onChange={(e) => onTogglePageSelectAll(e.target.checked)}
          disabled={!visibleOnPageCount}
          size="small"
        />
      }
      label={
        <Typography variant="caption" color="textSecondary">
          {allPageChecked
            ? "All vendors on this page selected"
            : "Select all on this page"}
        </Typography>
      }
    />
  </Box>
);

const ConfidenceFilterChips = ({
  selectedTier,
  setSelectedTier,
  counts,
  hideActioned,
  setHideActioned
}) => {
  const filters = [
    { label: "All", value: "all", count: counts.all },
    { label: "High", value: "high", count: counts.high },
    { label: "Medium", value: "medium", count: counts.medium },
    { label: "Low", value: "low", count: counts.low }
  ];

  return (
    <Grid container alignItems="center" spacing={1} wrap="wrap">
      {filters.map(({ label, value, count }) => (
        <Grid item key={`confidence-filter-${value}`}>
          <Chip
            label={`${label} (${count})`}
            color={selectedTier === value ? "primary" : "default"}
            variant={selectedTier === value ? "filled" : "outlined"}
            onClick={() => setSelectedTier(value)}
            size="small"
          />
        </Grid>
      ))}
      <Grid item style={{ marginLeft: "auto" }}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              Hide actioned
            </Typography>
          </Grid>
          <Grid item>
            <Switch
              size="small"
              checked={hideActioned}
              onChange={(e) => setHideActioned(e.target.checked)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const VendorCleanupViewBlock = ({ rootPath }) => {
  const history = useHistory();
  const location = useLocation();
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext();
  const {
    isLoading,
    error,
    clearError,
    data,
    counts,
    mergeCluster,
    mergingClusterIds,
    confirmZeroTxnCluster,
    dismissCluster,
    updateNaics,
    dismissNaics,
    archiveEmptyVendor,
    archiveZeroTxnPairs
  } = useCleanupCandidates();

  const pendingZeroTxnPairs = (data.zeroTxnPairs || []).filter(
    ({ status }) => status === "pending"
  );
  const [isArchivingZeroTxnPairs, setIsArchivingZeroTxnPairs] = useState(false);

  const onArchiveAllZeroTxnPairs = async () => {
    setIsArchivingZeroTxnPairs(true);
    try {
      await archiveZeroTxnPairs(
        pendingZeroTxnPairs.map(({ pairId }) => pairId)
      );
    } finally {
      setIsArchivingZeroTxnPairs(false);
    }
  };

  useEffect(() => {
    if (!error) {
      return;
    }
    activateSnackbar({ message: error, alert: "error" });
    clearError();
  }, [error, activateSnackbar, clearError]);

  const activeTab = readTabFromSearch(location.search);
  const onTabChange = (_, nextTab) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", nextTab);
    history.replace({ search: `?${params.toString()}` });
  };

  const [activeClusterId, setActiveClusterId] = useState(null);
  const activeCluster = activeClusterId
    ? data.duplicateClusters.find((c) => c.clusterId === activeClusterId)
    : null;
  const [activeNaicsVendor, setActiveNaicsVendor] = useState(null);
  const [selectedTier, setSelectedTier] = useState("all");
  const [hideActioned, setHideActioned] = useState(false);
  const [clusterSearch, setClusterSearch] = useState("");
  const [naicsSearch, setNaicsSearch] = useState("");
  const [selectedNaicsReason, setSelectedNaicsReason] = useState("all");

  const naicsReasonCounts = useMemo(
    () =>
      data.naicsIssues.reduce(
        (acc, { status, reason }) => {
          if (status !== "pending") {
            return acc;
          }
          return { ...acc, all: acc.all + 1, [reason]: (acc[reason] || 0) + 1 };
        },
        { all: 0, "low-confidence": 0, "merge-conflict": 0, "government-entity": 0 }
      ),
    [data.naicsIssues]
  );

  const clusterCounts = useMemo(() => {
    const baseCounts = { all: 0, high: 0, medium: 0, low: 0 };
    return data.duplicateClusters.reduce(
      (acc, { confidence, status }) => {
        if (status !== "pending") {
          return acc;
        }
        const tier = confidenceTier(confidence);
        return {
          ...acc,
          all: acc.all + 1,
          [tier]: acc[tier] + 1
        };
      },
      baseCounts
    );
  }, [data.duplicateClusters]);

  const normalizedClusterSearch = clusterSearch.trim().toLowerCase();
  const normalizedNaicsSearch = naicsSearch.trim().toLowerCase();

  const visibleClusters = data.duplicateClusters.filter((cluster) => {
    const tier = confidenceTier(cluster.confidence);
    const isActioned = cluster.status !== "pending";
    const tierMatch = selectedTier === "all" || tier === selectedTier;
    const actionMatch = !hideActioned || !isActioned;
    const searchMatch =
      !normalizedClusterSearch ||
      cluster.vendors.some(({ name }) =>
        name.toLowerCase().includes(normalizedClusterSearch)
      );
    return tierMatch && actionMatch && searchMatch;
  });

  const visibleNaicsIssues = data.naicsIssues.filter((vendor) => {
    const actionMatch = !hideActioned || vendor.status === "pending";
    const reasonMatch = selectedNaicsReason === "all" || vendor.reason === selectedNaicsReason;
    const displayIndustry = vendor.naicsTitle || formatCategoryLabel(vendor.emissionCategory);
    const searchMatch =
      !normalizedNaicsSearch ||
      vendor.name.toLowerCase().includes(normalizedNaicsSearch) ||
      displayIndustry.toLowerCase().includes(normalizedNaicsSearch);
    return actionMatch && reasonMatch && searchMatch;
  });

  const visibleEmptyVendors = data.emptyVendors.filter(
    ({ status }) => status === "pending"
  );
  const archivedEmptyVendors = data.emptyVendors.filter(
    ({ status }) => status === "archived"
  );
  const archivedEmptyCount = archivedEmptyVendors.length;

  const clusterPagination = usePagination(visibleClusters.length);
  const naicsPagination = usePagination(visibleNaicsIssues.length);
  const emptyPagination = usePagination(visibleEmptyVendors.length);
  const archivedPagination = usePagination(archivedEmptyVendors.length);

  const pagedClusters = visibleClusters.slice(
    clusterPagination.start,
    clusterPagination.end
  );
  const pagedNaicsIssues = visibleNaicsIssues.slice(
    naicsPagination.start,
    naicsPagination.end
  );
  const pagedEmptyVendors = visibleEmptyVendors.slice(
    emptyPagination.start,
    emptyPagination.end
  );
  const pagedArchivedEmpty = archivedEmptyVendors.slice(
    archivedPagination.start,
    archivedPagination.end
  );

  const [checkedEmptyVendorIds, setCheckedEmptyVendorIds] = useState([]);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);

  const pendingPageVendorIds = pagedEmptyVendors
    .filter(({ status }) => status === "pending")
    .map(({ relationshipId }) => relationshipId);
  const allPageChecked =
    pendingPageVendorIds.length > 0 &&
    pendingPageVendorIds.every((id) => checkedEmptyVendorIds.includes(id));
  const somePageChecked =
    !allPageChecked &&
    pendingPageVendorIds.some((id) => checkedEmptyVendorIds.includes(id));

  const toggleEmptyVendorChecked = (id) =>
    setCheckedEmptyVendorIds((current) =>
      current.includes(id)
        ? current.filter((existingId) => existingId !== id)
        : [...current, id]
    );

  const onTogglePageSelectAll = (nextChecked) => {
    if (nextChecked) {
      setCheckedEmptyVendorIds((current) => [
        ...current,
        ...pendingPageVendorIds.filter((id) => !current.includes(id))
      ]);
      return;
    }
    setCheckedEmptyVendorIds((current) =>
      current.filter((id) => !pendingPageVendorIds.includes(id))
    );
  };

  const onClearEmptySelection = () => setCheckedEmptyVendorIds([]);

  const onBulkArchiveEmpty = async () => {
    setIsBulkArchiving(true);
    try {
      await Promise.allSettled(
        checkedEmptyVendorIds.map((id) => archiveEmptyVendor(id))
      );
      setCheckedEmptyVendorIds([]);
    } finally {
      setIsBulkArchiving(false);
    }
  };

  const onBack = () => history.push(`${rootPath}/dashboard/vendors`);

  return (
    <Box p={3}>
      <Container maxWidth="lg">
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              color="inherit"
            >
              Back to vendors
            </Button>
          </Grid>
          <Grid item>
            <Grid container alignItems="center" spacing={2} wrap="nowrap">
              <Grid item>
                <Box
                  p={1.5}
                  style={{
                    backgroundColor: hexToRgba(palette.primary.main, 0.15),
                    borderRadius: 999,
                    display: "flex"
                  }}
                >
                  <CleaningServicesIcon color="primary" />
                </Box>
              </Grid>
              <Grid item xs>
                <Typography variant="h3">Vendor cleanup</Typography>
                <Typography variant="body1" color="textSecondary">
                  Resolve possible duplicate vendors, fix low-confidence
                  industry classifications, and archive vendors with no
                  transactions.
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {pendingZeroTxnPairs.length > 0 && (
            <Grid item>
              <Paper
                variant="outlined"
                style={{
                  borderColor: palette.primary.main,
                  backgroundColor: hexToRgba(palette.primary.main, 0.06)
                }}
              >
                <Box p={2}>
                  <Grid container alignItems="center" spacing={2} wrap="nowrap">
                    <Grid item xs>
                      <Typography variant="subtitle1">
                        {pendingZeroTxnPairs.length} empty-duplicate vendor
                        {pendingZeroTxnPairs.length === 1 ? "" : "s"} detected
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Each pair has one vendor with transactions and one
                        without — archiving the empty side hides the duplicate
                        without affecting your data.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isArchivingZeroTxnPairs}
                        onClick={onArchiveAllZeroTxnPairs}
                      >
                        {isArchivingZeroTxnPairs
                          ? "Archiving…"
                          : "Archive all"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item>
            <Box
              style={{
                borderBottom: `1px solid ${palette.backgroundGray.dark}`
              }}
            >
              <Tabs
                value={activeTab}
                onChange={onTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  value="duplicates"
                  label={`Possible duplicates (${counts.duplicateClusterCount})`}
                />
                <Tab
                  value="industries"
                  label={`Industry suggestions (${counts.naicsIssueCount})`}
                />
                <Tab
                  value="empty"
                  label={`No transactions (${counts.emptyVendorCount})`}
                />
                <Tab
                  value="archived"
                  label={`Archived (${archivedEmptyCount})`}
                />
              </Tabs>
            </Box>
          </Grid>

          {activeTab === "duplicates" && (
          <Grid item>
            <SectionPaper
              title="Possible duplicate vendors"
              subtitle={
                isLoading
                  ? "Loading…"
                  : `${counts.duplicateClusterCount} cluster${
                      counts.duplicateClusterCount === 1 ? "" : "s"
                    } pending review`
              }
              action={
                !isLoading && (
                  <ConfidenceFilterChips
                    selectedTier={selectedTier}
                    setSelectedTier={setSelectedTier}
                    counts={clusterCounts}
                    hideActioned={hideActioned}
                    setHideActioned={setHideActioned}
                  />
                )
              }
            >
              <Box mb={2}>
                <SearchInput
                  value={clusterSearch}
                  onChange={setClusterSearch}
                  placeholder="Search by vendor name"
                />
              </Box>
              {visibleClusters.length ? (
                <>
                  <Grid container direction="column" spacing={1.5}>
                    {pagedClusters.map((cluster) => (
                      <Grid
                        item
                        key={`duplicate-cluster-row-${cluster.clusterId}`}
                      >
                        <ClusterRow
                          cluster={cluster}
                          onReview={(c) => setActiveClusterId(c.clusterId)}
                          isMerging={mergingClusterIds.includes(
                            cluster.clusterId
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <PaginationControls
                    totalCount={visibleClusters.length}
                    {...clusterPagination}
                  />
                </>
              ) : (
                <EmptyState
                  icon={CheckCircleIcon}
                  message="No duplicate clusters matching the current filters."
                />
              )}
            </SectionPaper>
          </Grid>
          )}

          {activeTab === "industries" && (
          <Grid item>
            <SectionPaper
              title="Industry suggestions"
              subtitle={
                isLoading
                  ? "Loading…"
                  : `${counts.naicsIssueCount} vendor${
                      counts.naicsIssueCount === 1 ? "" : "s"
                    } need a classification review`
              }
              action={
                !isLoading && (
                  <NaicsReasonFilterChips
                    selectedReason={selectedNaicsReason}
                    setSelectedReason={setSelectedNaicsReason}
                    counts={naicsReasonCounts}
                  />
                )
              }
            >
              <Box mb={2}>
                <SearchInput
                  value={naicsSearch}
                  onChange={setNaicsSearch}
                  placeholder="Search by vendor or industry name"
                />
              </Box>
              {visibleNaicsIssues.length ? (
                <>
                  <Grid container direction="column" spacing={1.5}>
                    {pagedNaicsIssues.map((vendor) => (
                      <Grid item key={`naics-row-${vendor.issueId}`}>
                        <NaicsRow
                          vendor={vendor}
                          onReview={setActiveNaicsVendor}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <PaginationControls
                    totalCount={visibleNaicsIssues.length}
                    {...naicsPagination}
                  />
                </>
              ) : (
                <EmptyState
                  icon={CheckCircleIcon}
                  message="No low-confidence industry classifications need review."
                />
              )}
            </SectionPaper>
          </Grid>
          )}

          {activeTab === "empty" && (
          <Grid item>
            <SectionPaper
              title="Vendors with no transactions"
              subtitle={
                isLoading
                  ? "Loading…"
                  : `${counts.emptyVendorCount} vendor${
                      counts.emptyVendorCount === 1 ? "" : "s"
                    } you may want to archive`
              }
            >
              {visibleEmptyVendors.length ? (
                <>
                  {!!checkedEmptyVendorIds.length && (
                    <Box mb={2}>
                      <EmptyVendorsBulkActionBar
                        checkedCount={checkedEmptyVendorIds.length}
                        isBulkArchiving={isBulkArchiving}
                        onBulkArchive={onBulkArchiveEmpty}
                        onClearSelection={onClearEmptySelection}
                      />
                    </Box>
                  )}
                  <EmptyVendorsSelectAllHeader
                    allPageChecked={allPageChecked}
                    somePageChecked={somePageChecked}
                    onTogglePageSelectAll={onTogglePageSelectAll}
                    visibleOnPageCount={pendingPageVendorIds.length}
                  />
                  <Grid container direction="column" spacing={1.5}>
                    {pagedEmptyVendors.map((vendor) => (
                      <Grid item key={`empty-vendor-row-${vendor.vendorId}`}>
                        <EmptyVendorRow
                          vendor={vendor}
                          isChecked={checkedEmptyVendorIds.includes(
                            vendor.relationshipId
                          )}
                          onToggleChecked={toggleEmptyVendorChecked}
                          onArchive={archiveEmptyVendor}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <PaginationControls
                    totalCount={visibleEmptyVendors.length}
                    {...emptyPagination}
                  />
                </>
              ) : (
                <EmptyState
                  icon={CheckCircleIcon}
                  message="No empty vendors to archive right now."
                />
              )}
            </SectionPaper>
          </Grid>
          )}

          {activeTab === "archived" && (
          <Grid item>
            <SectionPaper
              title="Archived vendors"
              subtitle={
                isLoading
                  ? "Loading…"
                  : `${archivedEmptyCount} vendor${
                      archivedEmptyCount === 1 ? "" : "s"
                    } archived in this session`
              }
            >
              {archivedEmptyVendors.length ? (
                <>
                  <Grid container direction="column" spacing={1.5}>
                    {pagedArchivedEmpty.map((vendor) => (
                      <Grid
                        item
                        key={`archived-empty-row-${vendor.vendorId}`}
                      >
                        <EmptyVendorRow
                          vendor={vendor}
                          isChecked={false}
                          onToggleChecked={() => {}}
                          onArchive={() => {}}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <PaginationControls
                    totalCount={archivedEmptyVendors.length}
                    {...archivedPagination}
                  />
                </>
              ) : (
                <EmptyState
                  icon={CheckCircleIcon}
                  message="No vendors archived in this session. Archive empty vendors from the No transactions tab to see them here."
                />
              )}
            </SectionPaper>
          </Grid>
          )}
        </Grid>
      </Container>

      {activeClusterId && activeCluster && (
        activeCluster.isZeroTxnPair ? (
          <ZeroTxnClusterSlider
            cluster={activeCluster}
            onClose={() => setActiveClusterId(null)}
            onConfirmArchive={confirmZeroTxnCluster}
            onDismiss={dismissCluster}
            isConfirming={mergingClusterIds.includes(activeCluster.clusterId)}
          />
        ) : (
          <DuplicateClusterSlider
            cluster={activeCluster}
            onClose={() => setActiveClusterId(null)}
            onMerge={mergeCluster}
            onDismiss={dismissCluster}
          />
        )
      )}
      {activeNaicsVendor && (
        <NaicsReviewSlider
          vendor={activeNaicsVendor}
          onClose={() => setActiveNaicsVendor(null)}
          onUpdate={updateNaics}
          onDismiss={dismissNaics}
        />
      )}
    </Box>
  );
};

const VendorCleanupView = (props) => (
  <FeatureErrorBoundary featureName="Vendor Cleanup">
    <VendorCleanupViewBlock {...props} />
  </FeatureErrorBoundary>
);

export default VendorCleanupView;
